/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["silly"] = 0] = "silly";
    LogLevel[LogLevel["trace"] = 1] = "trace";
    LogLevel[LogLevel["debug"] = 2] = "debug";
    LogLevel[LogLevel["info"] = 3] = "info";
    LogLevel[LogLevel["warn"] = 4] = "warn";
    LogLevel[LogLevel["error"] = 5] = "error";
    LogLevel[LogLevel["fatal"] = 6] = "fatal";
    LogLevel[LogLevel["log"] = 7] = "log";
})(LogLevel || (LogLevel = {}));

var REDACTED_KEYWORDS = [
    'apikey',
    'accesstoken',
    'Authorization',
    'secret',
].map(function (k) { return k.toLowerCase(); });
var REDACTED_VALUE = '[REDACTED]';
var redact = function (info, options) {
    if (options === void 0) { options = { disableRedaction: false }; }
    if (options.disableRedaction)
        return info;
    var redactionKeyWords = options.keyWords
        ? REDACTED_KEYWORDS.concat(options.keyWords)
        : REDACTED_KEYWORDS;
    if (typeof info !== 'object') {
        return info;
    }
    else if (Array.isArray(info)) {
        return info.map(function (val) { return redact(val, options); });
    }
    else {
        return Object.keys(info).reduce(function (acc, key) {
            if (isKeyWordMatch(key, redactionKeyWords)) {
                acc[key] = options.redactedValue || REDACTED_VALUE;
            }
            else {
                acc[key] = info[key] ? redact(info[key], options) : info[key];
            }
            return acc;
        }, {});
    }
};
var isKeyWordMatch = function (key, keywords) {
    return keywords.map(function (k) { return k.toLowerCase(); }).includes(key.toLowerCase());
};

var InternalLogger = (function () {
    function InternalLogger(actualLogger, loggerOptions) {
        if (loggerOptions === void 0) { loggerOptions = {}; }
        this.actualLogger = actualLogger;
        this.loggerOptions = loggerOptions;
    }
    InternalLogger.prototype.redact = function (info) {
        return info ? redact(info, this.loggerOptions.redaction) : info;
    };
    InternalLogger.prototype.child = function (attributes) {
        return this.actualLogger.child(attributes, this.loggerOptions);
    };
    InternalLogger.prototype.silly = function (message, attributes) {
        this.emitLogMessage(LogLevel.silly, message, attributes);
    };
    InternalLogger.prototype.trace = function (message, attributes) {
        this.emitLogMessage(LogLevel.trace, message, attributes);
    };
    InternalLogger.prototype.debug = function (message, attributes) {
        this.emitLogMessage(LogLevel.debug, message, attributes);
    };
    InternalLogger.prototype.info = function (message, attributes) {
        this.emitLogMessage(LogLevel.info, message, attributes);
    };
    InternalLogger.prototype.warn = function (message, attributes) {
        this.emitLogMessage(LogLevel.warn, message, attributes);
    };
    InternalLogger.prototype.error = function (message, attributes) {
        this.emitLogMessage(LogLevel.error, message, attributes);
    };
    InternalLogger.prototype.fatal = function (message, attributes) {
        this.emitLogMessage(LogLevel.fatal, message, attributes);
    };
    InternalLogger.prototype.log = function (message, attributes) {
        this.emitLogMessage(LogLevel.log, message, attributes);
    };
    InternalLogger.prototype.emitLogMessage = function (logLevelId, message, attributes) {
        var logLevelType = LogLevel[logLevelId];
        if (this.actualLogger && this.loggerOptions.desiredLogLevel <= logLevelId) {
            this.actualLogger[logLevelType](message, this.redact(attributes));
        }
    };
    return InternalLogger;
}());

var ConsoleLoggerAdapter = (function () {
    function ConsoleLoggerAdapter(attributes) {
        this.attributes = attributes;
    }
    ConsoleLoggerAdapter.prototype.merge = function (attributes) {
        return __assign(__assign({}, this.attributes), attributes);
    };
    ConsoleLoggerAdapter.prototype.child = function (attributes, loggerOptions) {
        return new InternalLogger(new ConsoleLoggerAdapter(this.merge(attributes)), loggerOptions);
    };
    ConsoleLoggerAdapter.prototype.info = function (message, attributes) {
        this.emitLogMessage('info', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.warn = function (message, attributes) {
        this.emitLogMessage('warn', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.error = function (message, attributes) {
        this.emitLogMessage('error', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.fatal = function (message, attributes) {
        this.emitLogMessage('error', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.silly = function (message, attributes) {
        this.emitLogMessage('info', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.trace = function (message, attributes) {
        this.emitLogMessage('info', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.debug = function (message, attributes) {
        this.emitLogMessage('debug', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.log = function (message, attributes) {
        this.emitLogMessage('log', message, attributes);
    };
    ConsoleLoggerAdapter.prototype.emitLogMessage = function (logType, message, attributes) {
        attributes || this.attributes
            ? console[logType](message, this.merge(attributes))
            : console[logType](message);
    };
    return ConsoleLoggerAdapter;
}());

var NullLogger = (function () {
    function NullLogger() {
    }
    NullLogger.prototype.silly = function (message, attributes) { };
    NullLogger.prototype.trace = function (message, attributes) { };
    NullLogger.prototype.debug = function (message, attributes) { };
    NullLogger.prototype.info = function (message, attributes) { };
    NullLogger.prototype.warn = function (message, attributes) { };
    NullLogger.prototype.error = function (message, attributes) { };
    NullLogger.prototype.fatal = function (message, attributes) { };
    NullLogger.prototype.log = function (message, attributes) { };
    NullLogger.prototype.child = function (attributes) {
        return this;
    };
    return NullLogger;
}());

var isProduction = process.env.NODE_ENV === 'production';
var prefix = 'Invariant failed';
function invariant(condition, message) {
    if (condition) {
        return;
    }
    if (isProduction) {
        throw new Error(prefix);
    }
    var provided = typeof message === 'function' ? message() : message;
    var value = provided ? "".concat(prefix, ": ").concat(provided) : prefix;
    throw new Error(value);
}

var internalLogger = new InternalLogger(new NullLogger());
var initialized = false;
var warned = false;
function initializeLogger(logger, loggerOptions) {
    invariant(!initialized, 'Attempted to reinitialize logger');
    internalLogger = new InternalLogger(logger, loggerOptions);
    invariant(internalLogger, 'Failed to create a logger');
    initialized = true;
    return internalLogger;
}
function getLogger(attributes) {
    if (!initialized && !warned && process.env.NODE_ENV !== 'test') {
        warned = true;
        console.warn('internalLogger was not initialized');
    }
    if (attributes)
        return internalLogger.child(attributes);
    return internalLogger;
}
function getLibraryLoggingAttributes(name, version, additionalMetadata) {
    return __assign({ lib: {
            name: name,
            version: version,
        } }, additionalMetadata);
}
function getMfeLoggingAttributes(name, version, gatsbyPhase, environment, additionalMetadata) {
    return __assign({ mfe: {
            name: name,
            version: version,
            gatsbyPhase: gatsbyPhase,
            environment: environment,
        } }, additionalMetadata);
}
function initConsoleLogger(attributes, loggerOptions) {
    var loggingAdapter = new ConsoleLoggerAdapter(attributes);
    var logger = initializeLogger(loggingAdapter, loggerOptions);
    logger.debug('Console logger initialized');
}
function initCustomLogger(loggingAdapter, loggerOptions) {
    var logger = initializeLogger(loggingAdapter, loggerOptions);
    logger.debug('Custom logger initialized');
}

var Dispenser = (function () {
    function Dispenser(createInstance) {
        this.createInstance = createInstance;
    }
    Dispenser.prototype.getInstance = function () {
        if (!this.instance) {
            this.instance = this.createInstance();
        }
        return this.instance;
    };
    return Dispenser;
}());

export { ConsoleLoggerAdapter, Dispenser, InternalLogger, LogLevel, NullLogger, getLibraryLoggingAttributes, getLogger, getMfeLoggingAttributes, initConsoleLogger, initCustomLogger, initializeLogger };
